// Access & RBAC Settings — PSD §11.1, §11.2 (F11.1, F11.2). Settings screen.

import { Check, Minus } from 'lucide-react';
import { useStore } from '../../store/selectors';
import type { Role, Permission } from '../../auth/roles';
import { hasPermission } from '../../auth/roles';
import { Tooltip } from '../common/Tooltip';

const ROLES: { value: Role; label: string }[] = [
  { value: 'viewer', label: 'Viewer (read-only, export)' },
  { value: 'editor', label: 'Editor (create/run/edit)' },
  { value: 'reviewer', label: 'Reviewer (edit + sign-off)' },
  { value: 'approver', label: 'Approver (full access)' },
];

const PERMISSIONS: Permission[] = [
  'valuation:create',
  'valuation:edit',
  'valuation:run',
  'signoff:submit',
  'signoff:approve',
  'settings:edit',
  'export:download',
];

export function AccessSection() {
  const settings = useStore((s) => s.settings);
  const setRbacRole = useStore((s) => s.setRbacRole);
  const toggleCSP = useStore((s) => s.toggleClientSideProcessing);

  return (
    <div className="settings-section">
      <h3>Access &amp; RBAC</h3>

      <div className="form-group">
        <label htmlFor="role">
          Role
          <Tooltip
            label="Local RBAC role controlling UI permissions (this is a single-user SPA — there is no authentication backend). Reviewer and Approver can sign off; Viewer is read-only."
            ariaLabel="Help: Role"
          />
        </label>
        <select
          id="role"
          value={settings.rbacRole}
          onChange={(e) => setRbacRole(e.target.value as Role)}
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <table className="data-table mt-3">
        <thead>
          <tr>
            <th>Permission</th>
            <th>Granted</th>
          </tr>
        </thead>
        <tbody>
          {PERMISSIONS.map((p) => (
            <tr key={p}>
              <td>
                <code>{p}</code>
              </td>
              <td>{hasPermission(settings.rbacRole, p) ? <Check size={14} aria-label="granted" /> : <Minus size={14} aria-label="not granted" />}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="form-group mt-4">
        <label htmlFor="csp">
          <input
            id="csp"
            type="checkbox"
            checked={settings.clientSideProcessing}
            onChange={toggleCSP}
          />{' '}
          Client-Side Processing Only
        </label>
        <p className="field-hint">
          When enabled, all calculations run in the browser. No data is sent to any server.
        </p>
      </div>
    </div>
  );
}
